/**
 * Central registry of browser-storage keys.
 *
 * Every key used with the storage library is declared here as a descriptor that pairs the
 * raw string with its scope, so the tab-vs-shared decision lives in ONE place (see Scopes below).
 *
 * Consumers never name a scope: they pass a descriptor to the resolver (`Storage.get`,
 * `Storage.set`, …) which reads `scope` off the descriptor. Re-scoping a key is a one-line
 * change here. Dynamic keys (suffixed by language, entity or instance) are builder
 * functions returning the same `{ key, scope }` shape.
 *
 * Scopes:
 *   - tabKey    -> sessionStorage: per-tab working state.
 *   - sharedKey -> localStorage: cross-tab / long-term state.
 *   - seededKey -> per-tab working state (stored exactly like tabKey) that is ALSO part of the
 *     new-tab handoff: when a candidate is opened in a new tab, the handoff (storage/handoff.js)
 *     copies these keys from the ORIGIN tab into the new one, so the prev/next arrows, the
 *     counter and selection navigation keep working. Nothing is mirrored on write and a fresh
 *     listing tab never seeds, so a selection cannot leak into an unrelated tab.
 *
 * Some legacy plumbing (arrow_navi.js, shopping-cart.js) sources these same descriptors
 * from `window.TAStorageKeys` rather than duplicating the strings.
 */

export const SCOPE_TAB = 'tab';
export const SCOPE_SHARED = 'shared';
export const SCOPE_SEEDED = 'seeded';

const tabKey = (key) => ({ key, scope: SCOPE_TAB });
const sharedKey = (key) => ({ key, scope: SCOPE_SHARED });
const seededKey = (key) => ({ key, scope: SCOPE_SEEDED });

export const StorageKeys = {
    // ---- TAB scope (sessionStorage): per-page working state ----

    // Full listing search state ({ plan, values, filters }); read on load, written on apply.
    // Applicant/talent search state is seeded so arrow_navi can re-fetch the nav batch in a
    // new candidate-detail tab; the other listings have no detail navigation and stay tab-only.
    applicationsSearchState: (lang) => seededKey(`talentadore_search_state_${lang}`),
    positionsSearchState: (lang) => tabKey(`talentadore_positions_search_state_${lang}`),
    talentsSearchState: (lang) => seededKey(`talentadore_talent_search_state_${lang}`),
    tcGroupsSearchState: (lang) => tabKey(`talentadore_tc_groups_search_state_${lang}`),
    requisitionsSearchState: (lang) => tabKey(`talentadore_jobrequisitions_search_state_${lang}`),
    reEmploymentSearchState: (lang) => tabKey(`talentadore_re_employment_search_state_${lang}`),
    // Generic activity-log listing search state (id is the datatable identifier).
    logsSearchState: (id, lang) => tabKey(`talentadore_${id}_logs_search_state_${lang}`),
    // Calendar event-type filter state.
    calendarEventTypesState: tabKey('talentadore_calendar_event_types_state'),

    // Result id batches + paging, consumed by prev/next arrow navigation on detail pages.
    // Applicant/talent batches are seeded so the arrows + counter render when a candidate is
    // opened in a new tab; positions/tc-groups/requisitions have no detail navigation.
    applicationsBatch: seededKey('talentadore_talents'),
    applicationsPaging: seededKey('talentadore_talents_paging'),
    talentsInTcBatch: seededKey('talentadore_talents_list_in_tc'),
    talentsInTcPaging: seededKey('talentadore_talents_paging_in_tc'),
    positionsBatch: tabKey('talentadore_positions'),
    positionsPaging: tabKey('talentadore_positions_paging'),
    tcGroupsBatch: tabKey('talentadore_tc_groups'),
    tcGroupsPaging: tabKey('talentadore_tc_groups_paging'),
    requisitionsBatch: tabKey('talentadore_jobrequisitions'),
    requisitionsPaging: tabKey('talentadore_jobrequisitions_paging'),

    // Mass-action selections (shopping cart / arrow navigation).
    // Applicant/talent selection nav sets are seeded so selection-mode arrows keep working when
    // a selected candidate is opened in a new tab (the handoff copies them from the origin tab,
    // see handoff.js). A fresh listing tab never seeds, so a selection never silently drives a
    // mass action in an unrelated tab.
    applicationsSelection: seededKey('talents_selection'),
    applicationsSelectionBatch: seededKey('talents_selection_batch'),
    applicationsSelectionPaging: seededKey('talents_selection_paging'),
    applicationsSelectionSearchState: (lang) => seededKey(`talentadore_selection_search_state_${lang}`),
    communitySelection: seededKey('community_talents_selection'),
    communitySelectionBatch: seededKey('community_talents_selection_batch'),
    communitySelectionPaging: seededKey('community_talents_selection_paging'),
    communitySelectionSearchState: (lang) => seededKey(`talentadore_community_selection_search_state_${lang}`),
    tcGroupsSelection: tabKey('talent_community_groups_selection'),
    tcGroupsSelectionBatch: tabKey('talent_community_groups_selection_batch'),
    tcGroupsSelectionPaging: tabKey('talent_community_groups_selection_paging'),
    tcGroupsSelectionSearchState: (lang) => tabKey(`talent_community_groups_selection_search_state_${lang}`),
    // Suggested-community-talents selection bucket (create-job talent picker).
    suggestedCommunityTalentsSelection: tabKey('suggested_community_talents_selection'),

    // Currently selected position filter value; shared between searchform, add-application and shopping cart.
    selectedPositions: tabKey('selectedPositions'),

    // Per-DataTables-instance persisted table state (prefix + settings.sInstance).
    dataTablesState: (prefix, instance) => tabKey(`${prefix}${instance}`),

    // Dashboard widget saved filters.
    widgetFilters: (widgetId, id) => tabKey(`widget_filters_${widgetId}_${id}`),

    // Authenticated user hash stamped into tab scope. A mismatch on load means the tab is
    // being reused by a different user, so the tab scope is purged (see storage/auth_purge.js).
    authUserStamp: tabKey('auth_user_stamp'),

    // Already tab-scoped (sessionStorage) before this work; centralized here for reuse.
    flashNotification: tabKey('flash_notification'),
    flashNotificationType: tabKey('flash_notification_type'),
    flashNotificationDuration: tabKey('flash_notification_duration'),
    svgIconsCache: tabKey('svgIconsCache'),
    openRecruitmentFlowModal: tabKey('openRecruitmentFlowModal'),

    // ---- SHARED scope (localStorage): cross-tab preferences / long-term flags ----

    applicationsViewType: sharedKey('applications_view_type'),
    // Sidebar collapsed flag; the actual key is `${prefix}search_sidebar_collapsed`.
    sidebarCollapsed: (prefix) => sharedKey(`${prefix}search_sidebar_collapsed`),
    evaluationSettings: sharedKey('talentadore_evaluation_setting_obj'),
    tagsPageLength: (tab) => sharedKey(`tags-page-length-${tab}`),
    calendarSelectedColleagues: sharedKey('selected_colleagues'),
    calendarHasColleaguePreference: sharedKey('has_colleague_preference'),
    calendarWeekdaysOnly: sharedKey('talentadore_calendar_weekdays_only'),
    overlayWidth: sharedKey('overlayWidth'),
    anotherStepOverlay: sharedKey('evaluation-another-step-overlay'),
    hideDeleteSkillOverlay: sharedKey('hide-delete-skill-overlay'),
    conversationalChatOpen: sharedKey('conv_ui_chat_open'),
    conversationalChatId: sharedKey('conv_ui_chat_id'),
    welcomeBannerClosed: sharedKey('welcome-banner-closed'),
    // Map of user id -> true; see storage/store/scheduling.js for why it is keyed per user.
    schedulingDefaultAvailabilityNoticeDismissed: sharedKey('scheduling-default-availability-dismissed'),
    // Map of user id -> { section id -> expanded }; see storage/store/applicant_view.js.
    applicantViewSectionsExpanded: sharedKey('applicant-view-sections-expanded'),

    // Cross-tab mass-action lock, keyed per shopping-cart context (applications / talent-community)
    // so the two listings lock independently. Read by the `storage` event handler in
    // legacy_js/shopping-cart-mass-action.js — the produced string must stay stable.
    massActionLock: (context) => sharedKey(`mass_action_lock_${context}`),
};

/**
 * Which stored keys make up the new-tab handoff set (every `seededKey` in the registry),
 * expressed as raw-string matchers so the handoff can pick them out of sessionStorage without
 * knowing the current language. Static seeded keys match exactly; language-suffixed builders
 * contribute a prefix (the builder called with an empty language). Derived from the registry so
 * the seeded scope stays the single source of truth.
 *
 * :return: `{ exact: [key], prefixes: [keyPrefix] }`
 */
export function getSeedKeyMatchers() {
    const exact = [];
    const prefixes = [];
    Object.values(StorageKeys).forEach((entry) => {
        if (typeof entry === 'function') {
            let descriptor;
            try {
                descriptor = entry('');
            } catch {
                return;
            }
            if (descriptor && descriptor.scope === SCOPE_SEEDED) prefixes.push(descriptor.key);
        } else if (entry && entry.scope === SCOPE_SEEDED) {
            exact.push(entry.key);
        }
    });
    return { exact, prefixes };
}

export default StorageKeys;
